import { useMemo, type ReactNode } from 'react';
import { formatClock } from '@nest-recruit/shared';
import { typedTime } from '../lib/typed-time';
import { Select, type SelectOption } from './Select';

// Ours, never `<input type="time">`, whose look follows the browser's locale. Adapted from
// Tomo's TimePicker — our Select over quarter hours. Value is `HH:mm`; labels read with
// am/pm, "3:45 pm" (product owner, 2026-10-25, D10: 03:45 was picked for an afternoon
// interview). The list runs 12:00 am to 11:45 pm, in order.
// Any other minute is typed into the filter box (owner, 2026-10-14: "a custom time like 10.6").

const STEP_MINUTES = 15;
const pad = (value: number): string => String(value).padStart(2, '0');

/** One `HH:mm` as a list option. The filter also matches the 24-hour form, so typing "15:45"
 *  finds 3:45 pm, as do "3:45p" and "3:45 pm". */
export function timeOption(value: string): SelectOption {
  const label = formatClock(value);
  return { value, label, searchText: `${label} ${value}` };
}

const QUARTER_HOURS: readonly SelectOption[] = Array.from({ length: (24 * 60) / STEP_MINUTES }, (_, index) => {
  const minutes = index * STEP_MINUTES;
  return timeOption(`${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`);
});

function typedOptionOf(query: string): SelectOption | null {
  const value = typedTime(query);
  return value ? timeOption(value) : null;
}

interface TimePickerProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly id?: string;
  readonly label: string;
  readonly isInvalid?: boolean;
  readonly disabled?: boolean;
  readonly describedBy?: string;
}

export function TimePicker({ value, onChange, id, label, isInvalid, disabled, describedBy }: TimePickerProps): ReactNode {
  // A stored time off the quarter hours (09:45 is on them; 09:50 is not) still needs its option.
  const options = useMemo(
    () => (value === '' || QUARTER_HOURS.some((option) => option.value === value) ? QUARTER_HOURS : [timeOption(value), ...QUARTER_HOURS]),
    [value],
  );
  return (
    <Select
      id={id}
      label={label}
      value={value === '' ? null : value}
      onChange={onChange}
      options={options}
      placeholder="Time"
      typedOption={typedOptionOf}
      {...(isInvalid === undefined ? {} : { isInvalid })}
      {...(disabled === undefined ? {} : { disabled })}
      {...(describedBy === undefined ? {} : { describedBy })}
    />
  );
}
